import { useState } from 'react';
import { Tag, Pause, Play, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLanguage } from '@/contexts/language';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';
import { useOrganization } from '@/contexts/OrganizationContext';

interface ProfileCardMenuProps {
  profileId: string;
  profileName: string;
  onCategoryEdit: () => void;
  onSuspendSuccess?: () => void;
  isSuspended?: boolean;
}

export const ProfileCardMenu = ({
  profileId,
  profileName,
  onCategoryEdit,
  onSuspendSuccess,
  isSuspended = false,
}: ProfileCardMenuProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganization: organization } = useOrganization();
  const [showSuspendConfirmation, setShowSuspendConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const handleSuspend = async () => {
    setSuspending(true);
    try {
      // Refresh session to ensure valid token
      await supabase.auth.refreshSession();

      const { error } = await supabase
        .from('applicant_profiles')
        .update({
          is_active: false,
          suspended_at: new Date().toISOString()
        })
        .eq('id', profileId);

      if (error) throw error;

      toast({
        title: t('profile.suspend.suspended'),
        description: `"${profileName}" ${t('profile.suspend.warning')}`,
      });

      // Invalidate profiles cache to remove suspended profile from dashboard
      // Invalidate all 'profiles' queries to ensure UI updates across all views
      await queryClient.invalidateQueries({
        queryKey: ['profiles'],
      });

      setShowSuspendConfirmation(false);

      // Call the success callback to close the dialog and refresh the list
      onSuspendSuccess?.();
    } catch (error) {
      logger.error('Profile suspension failed:', error);
      toast({
        title: t('common.error'),
        description: t('profile.suspend.error'),
        variant: 'destructive',
      });
    } finally {
      setSuspending(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      // Refresh session to ensure valid token
      await supabase.auth.refreshSession();

      const { error } = await supabase
        .from('applicant_profiles')
        .update({
          is_active: true,
          suspended_at: null
        })
        .eq('id', profileId);

      if (error) throw error;

      toast({
        title: t('profile.reactivate.success'),
        description: `"${profileName}" ${t('profile.reactivate.description')}`,
      });

      // Invalidate profiles cache to refresh lists
      await queryClient.invalidateQueries({
        queryKey: ['profiles'],
      });

      // Call the success callback to close the dialog and refresh the list
      onSuspendSuccess?.();
    } catch (error) {
      logger.error('Profile reactivation failed:', error);
      toast({
        title: t('common.error'),
        description: t('profile.reactivate.error'),
        variant: 'destructive',
      });
    } finally {
      setReactivating(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Refresh session to ensure valid token
      await supabase.auth.refreshSession();

      const { error } = await supabase
        .from('applicant_profiles')
        .delete()
        .eq('id', profileId);

      if (error) throw error;

      toast({
        title: t('profile.delete.deleted'),
        description: `"${profileName}" ${t('profile.delete.success')}`,
      });

      // Invalidate profiles cache
      await queryClient.invalidateQueries({
        queryKey: ['profiles'],
      });

      setShowDeleteConfirmation(false);

      // Call the success callback to close the dialog and refresh the list
      onSuspendSuccess?.();
    } catch (error) {
      logger.error('Profile deletion failed:', error);
      toast({
        title: t('common.error'),
        description: t('profile.delete.error'),
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {isSuspended ? (
            // For suspended profiles: show Reactivate and Delete options
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReactivate();
                    }}
                    disabled={reactivating}
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{reactivating ? 'Réactivation...' : 'Réactiver'}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirmation(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Supprimer le profil</p>
                </TooltipContent>
              </Tooltip>
            </>
          ) : (
            // For active profiles: show Modify and Suspend options
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCategoryEdit();
                    }}
                  >
                    <Tag className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Modifier la catégorie</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-950/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSuspendConfirmation(true);
                    }}
                  >
                    <Pause className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Suspendre le profil</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </TooltipProvider>

      {showSuspendConfirmation && (
        <DeleteConfirmationModal
          isOpen={showSuspendConfirmation}
          profileName={profileName}
          isDeleting={suspending}
          onConfirm={handleSuspend}
          onCancel={() => setShowSuspendConfirmation(false)}
          mode="suspend"
        />
      )}

      {showDeleteConfirmation && (
        <DeleteConfirmationModal
          isOpen={showDeleteConfirmation}
          profileName={profileName}
          isDeleting={deleting}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          mode="delete"
        />
      )}
    </>
  );
};
